// ============================================================
// Moteur PUR des QUOTAS ANNUELS d'absence — AUCUNE dépendance Angular.
// Testé sans TestBed : hr-absence-quota.spec.ts.
// ============================================================
//
// « Un jour de congé rémunéré est accordé, sous certificat médical, aux salariés de la branche
//   ayant au moins 2 ans d'ancienneté et dont l'enfant âgé de 16 ans et moins serait malade. »
//   — CCN 1982 (négoce médico-technique), accord du 22 mai 2014, article 4.
//
// Une phrase, et trois mécaniques que rien ne portait jusqu'ici :
//
//   UN PLAFOND       le droit s'épuise. Une fois le jour posé, il n'y en a pas d'autre — et c'est
//                    au moment de la DEMANDE qu'il faut le dire, pas au refus du responsable.
//   UNE PÉRIODE      « par année civile » : le compteur se remet à zéro le 1er janvier, et le
//                    reliquat ne se reporte PAS. D'autres accords calent le droit sur la période
//                    de référence des congés payés — les deux sont prévus.
//   UNE ANCIENNETÉ   le droit s'ouvre au bout de 2 ans. Avant, le salarié n'a rien : autant le
//                    lui dire avec la date à laquelle il l'aura, plutôt qu'un « non » sec.
//
// Le moteur ne connaît aucune convention en particulier : il applique le quota qu'on lui donne
// (hr-absence-catalog.ts pour la valeur de branche, `data.quota` du référentiel c_values 23 pour
// la valeur de l'établissement, qui l'emporte toujours — une convention plus favorable, un accord
// d'entreprise, un usage maison se règlent là sans toucher au code).
//
// GARDE-FOU DE SAISIE, PAS RÈGLE DE DROIT : comme le compte de récupération (hr-rcr.ts), le refus
// s'affiche avant l'envoi et le responsable RH garde le dernier mot.

import { C_value } from '@app/shared/_models/common';
import { HR_ABSENCE_STATE_APPROVED, HR_ABSENCE_STATE_REQUESTED } from '@app/shared/constants';
import { AbsenceQuota, hrAbsenceProfileOf } from './hr-absence-catalog';
import { hrCountedLeaveDays } from './hr-leave';
import { DatRange, LeaveRules, leaveReferencePeriodOf } from './hr-leave-rights';

export type { AbsenceQuota } from './hr-absence-catalog';

// ============================================================
// Le quota applicable à un type du référentiel
// ============================================================

// Ne laisse passer qu'un quota EXPLOITABLE : un plafond négatif, une période inventée ou une
// ancienneté en toutes lettres venue d'une reprise de données ne doivent pas décider d'un droit.
// Un plafond à 0 (ou absent) veut dire « pas de compteur », pas « aucun jour » : on rend null,
// et l'absence redevient un congé ordinaire.
export function sanitizeAbsenceQuota(raw: any): AbsenceQuota | null {
	if (!raw || typeof raw !== 'object') return null;
	const days = +raw.days;
	if (isNaN(days) || days <= 0) return null;
	const seniority = +raw.seniority_months;
	return {
		days,
		period: raw.period === 'reference' ? 'reference' : 'civil',
		seniority_months: isNaN(seniority) || seniority < 0 ? 0 : Math.round(seniority),
	};
}

/// Le plafond RÉELLEMENT applicable à un type d'absence de l'établissement, dans l'ordre :
///   1. `data.quota = null` — « sans plafond », décidé EXPRÈS par le gérant. Il faut ce sentinel
///      pour qu'on puisse retirer un plafond que le catalogue, lui, continue de proposer ;
///   2. `data.quota` — ce qu'il a saisi dans « Établissements & services » ;
///   3. le quota du profil de catalogue auquel le type est rattaché (le droit de la branche) ;
///   4. rien — l'absence n'est pas plafonnée.
export function absenceQuotaOf(value: C_value | null | undefined): AbsenceQuota | null {
	const raw = value?.data?.quota;
	if (raw === null) return null;
	const own = sanitizeAbsenceQuota(raw);
	if (own) return own;
	return hrAbsenceProfileOf(value).profile.quota ?? null;
}

// ============================================================
// La période du droit
// ============================================================

/// La période de décompte qui contient `dat` : l'année civile, ou la période de référence des
/// congés payés de l'établissement quand l'accord y aligne le droit.
export function absenceQuotaPeriodOf(dat: string, quota: AbsenceQuota, rules: LeaveRules): DatRange {
	if (quota.period === 'reference') return leaveReferencePeriodOf(dat, rules);
	const year = dat.slice(0, 4);
	return { from: year + '-01-01', to: year + '-12-31' };
}

/// « du 01/01/2026 au 31/12/2026 » — la période telle qu'elle sera décomptée, en clair.
export function absenceQuotaPeriodLabel(p: DatRange): string {
	const fr = (d: string) => d.slice(8, 10) + '/' + d.slice(5, 7) + '/' + d.slice(0, 4);
	return 'du ' + fr(p.from) + ' au ' + fr(p.to);
}

// Date d'échéance d'une ancienneté : embauche + n mois, en calendaire. Le 31 janvier + 1 mois
// donne le 28 (ou 29) février, jamais le 3 mars — c'est ainsi qu'on compte une ancienneté.
export function addMonthsDat(dat: string, months: number): string {
	const y = +dat.slice(0, 4), m = +dat.slice(5, 7), d = +dat.slice(8, 10);
	const total = (y * 12 + (m - 1)) + months;
	const ny = Math.floor(total / 12), nm = (total % 12) + 1;
	const lastDay = new Date(Date.UTC(ny, nm, 0)).getUTCDate();
	const nd = Math.min(d, lastDay);
	return ny + '-' + String(nm).padStart(2, '0') + '-' + String(nd).padStart(2, '0');
}

// ============================================================
// Le compte
// ============================================================

export interface QuotaAccount {
	quota: AbsenceQuota;
	period: DatRange;
	used_days: number;					// jours APPROUVÉS sur la période
	pending_days: number;				// demandes en attente de réponse
	remaining_days: number;			// ce qu'il reste à poser (jamais négatif)
	eligible: boolean;					// l'ancienneté est-elle atteinte à la date de la demande ?
	eligible_from: string | null;	// la date à laquelle elle le sera — null si aucune condition
}

/// Le compte d'UN salarié sur UN type plafonné, à la date `dat`.
///
/// `absences` : les absences du salarié **de ce type**, tous états confondus — le tri par état se
/// fait ici, parce que compter l'ENGAGÉ est ce qui empêche de poser deux fois le même droit en
/// attendant que le responsable réponde (la même leçon que le compte de récupération).
///
/// `hireDate` : la date d'embauche (`hr_employee.hire_date`). Absente, on considère l'ancienneté
/// acquise : mieux vaut laisser passer une demande qu'inventer un refus sur une donnée manquante.
export function absenceQuotaAccountOf(
	quota: AbsenceQuota,
	absences: { datestart: string; dateend: string; state?: number; rest_days?: number[] | null; duration_kind?: number; hours?: number | null }[],
	dat: string,
	hireDate: string | null | undefined,
	rules: LeaveRules,
	holidays: Map<string, string>,
): QuotaAccount {
	const period = absenceQuotaPeriodOf(dat, quota, rules);
	const sum = (state: number) => hrCountedLeaveDays(
		(absences ?? []).filter(a => (a.state ?? HR_ABSENCE_STATE_REQUESTED) === state),
		period.from, period.to, holidays,
	).reduce((total, d) => total + d.days, 0);

	const used = sum(HR_ABSENCE_STATE_APPROVED);
	const pending = sum(HR_ABSENCE_STATE_REQUESTED);

	const eligibleFrom = hireDate && quota.seniority_months > 0
		? addMonthsDat(hireDate, quota.seniority_months)
		: null;

	return {
		quota,
		period,
		used_days: used,
		pending_days: pending,
		remaining_days: Math.max(0, quota.days - used - pending),
		eligible: eligibleFrom === null || dat >= eligibleFrom,
		eligible_from: eligibleFrom,
	};
}

/// La demande tient-elle dans le droit ? Rend le motif de refus, ou null.
/// `costDays` est le décompte de la demande (hrLeaveBreakdown.leave_days) : repos hebdomadaires et
/// fériés exclus, une demi-journée valant 0,5 — un droit d'un jour couvre donc deux demi-journées.
export function absenceQuotaIssue(costDays: number, account: QuotaAccount, typeName?: string): string | null {
	const label = typeName?.trim() || 'Ce congé';

	if (!account.eligible) {
		return label + ' demande ' + seniorityLabel(account.quota.seniority_months)
			+ ' d\'ancienneté : le droit s\'ouvrira le ' + frDat(account.eligible_from!) + '.';
	}
	if (costDays <= 0) return null;		// rien de décompté (que du repos, un férié) : rien à plafonner
	if (costDays > account.remaining_days) {
		const detail = account.pending_days > 0
			? ' (' + quotaDaysLabel(account.pending_days) + ' déjà en attente de réponse)'
			: '';
		return label + ' est plafonné à ' + quotaDaysLabel(account.quota.days) + ' ' + periodWording(account.quota)
			+ ' : il reste ' + quotaDaysLabel(account.remaining_days) + detail
			+ ', ' + quotaDaysLabel(costDays) + ' demandé' + (costDays > 1 ? 's' : '') + '.';
	}
	return null;
}

// « ½ jour », « 1 jour », « 2,5 jours » — le décompte s'écrit comme sur une fiche de paie.
export function quotaDaysLabel(days: number): string {
	if (days === 0.5) return '½ jour';
	const n = Math.round(days * 100) / 100;
	return n.toString().replace('.', ',') + ' jour' + (n > 1 ? 's' : '');
}

// « 2 ans » plutôt que « 24 mois » : c'est ainsi que la convention l'écrit, et que le salarié
// le comprend. Les durées qui ne tombent pas juste restent en mois.
export function seniorityLabel(months: number): string {
	if (months >= 12 && months % 12 === 0) {
		const years = months / 12;
		return years + ' an' + (years > 1 ? 's' : '');
	}
	return months + ' mois';
}

function periodWording(quota: AbsenceQuota): string {
	return quota.period === 'reference' ? 'par période de référence' : 'par année civile';
}

function frDat(dat: string): string {
	return dat.slice(8, 10) + '/' + dat.slice(5, 7) + '/' + dat.slice(0, 4);
}
